'use client';

import { IconHourglassEmpty } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import {
  ClaimOfferMinimalDetail,
  FetchClaimDetailResponseBody,
  ListClaimOffersResponseBody,
} from 'bff';

import { ReactNode } from 'react';
import { Button } from 'ui';
import { EmptyClaimTabContent } from './ClaimTabContent';
import { ClaimOffer, ClaimOfferList } from '..';
import { withEnablement } from '../hocs/withEnablement';

export const buildOffersQueryKey = (claimId: string) =>
  `claim-${claimId}-offers`;

const UnsafeAnswerOfferButton = ({
  offer,
}: {
  offer: ClaimOfferMinimalDetail;
}) => {
  return (
    <a href={`/claim/offer/${offer.id}?token=${offer.token}`} target='_blank'>
      <Button className='text-xs' size='sm'>
        Responder
      </Button>
    </a>
  );
};

const AnswerOfferButton = withEnablement(UnsafeAnswerOfferButton);

type RenderOfferActions = (offer: ClaimOfferMinimalDetail) => ReactNode;

const OfferItem = ({
  offer,
  created_by,
  canAnswer,
  renderOfferActions,
}: {
  offer: ClaimOfferMinimalDetail;
  created_by: FetchClaimDetailResponseBody['created_by'];
  prefixPath: string;
  canAnswer: boolean;
  renderOfferActions?: RenderOfferActions;
}) => (
  <ClaimOffer
    audience='customer'
    offer={offer}
    claim={{ created_by }}
    withCallToAction={false}
    actions={
      canAnswer && offer.status === 'pending' ? (
        <AnswerOfferButton offer={offer} />
      ) : (
        renderOfferActions?.(offer) ?? null
      )
    }
  />
);

export const ClaimOwnerOffersDataTable = ({
  initialData,
  listOffers,
  queryKey,
  canAnswer = true,
  ...props
}: {
  initialData: ListClaimOffersResponseBody;
  listOffers: () => Promise<ListClaimOffersResponseBody>;
  queryKey: string;
  created_by: FetchClaimDetailResponseBody['created_by'];
  prefixPath: string;
  canAnswer?: boolean;
  /** Extra actions of an offer that cannot be answered anymore (e.g. signing its agreement). */
  renderOfferActions?: RenderOfferActions;
}) => {
  const query = useQuery({
    queryKey: [buildOffersQueryKey(queryKey)],
    queryFn: () => listOffers(),
    initialData,
  });

  if (!query.data.offers.length)
    return <EmptyClaimTabContent label='Sin ofrecimiento recibido' />;

  return (
    <ClaimOfferList>
      {query.data.offers.map((offer, index) => (
        <OfferItem key={index} {...props} canAnswer={canAnswer} offer={offer} />
      ))}
    </ClaimOfferList>
  );
};
